import type {
  AttachmentPointDefinition,
  BuildRecipe,
  Catalog,
  CatalogModule,
  GlobalLivery,
} from "../core/index.ts";

export type CatalogCategoryId =
  | "locomotion"
  | "cockpit"
  | "weapon"
  | "backpack"
  | "shoulder"
  | "equipment";

export interface ModulePresentation {
  readonly category: CatalogCategoryId;
  readonly previewRef: string;
  readonly sourceId: string;
  readonly family: "spider" | "tracks" | "tank" | "buggy" | "universal";
}

export const categoryLabels: Readonly<Record<CatalogCategoryId, string>> = {
  locomotion: "Ходовые",
  cockpit: "Кабины",
  weapon: "Оружие",
  backpack: "Ранцы",
  shoulder: "Плечи и щиты",
  equipment: "Оснастка",
};

export const defaultLivery: GlobalLivery = {
  paletteId: "oxide-red",
  primary: "#a63d2e",
  accent: "#d7c8a4",
  emission: 0.16,
};

const baseTop = (): readonly AttachmentPointDefinition[] => [
  {
    id: "top",
    label: "Верхняя платформа",
    visualAnchor: "socket_top",
    accepts: ["cockpit", "shoulder-frame"],
  },
];

const regularCockpitPoints = (): readonly AttachmentPointDefinition[] => [
  {
    id: "weapon-left",
    label: "Левое вооружение",
    visualAnchor: "socket_weapon_l",
    accepts: ["weapon"],
  },
  {
    id: "weapon-right",
    label: "Правое вооружение",
    visualAnchor: "socket_weapon_r",
    accepts: ["weapon"],
  },
  {
    id: "weapon-top",
    label: "Верхнее вооружение",
    visualAnchor: "socket_weapon_top",
    accepts: ["weapon"],
  },
  {
    id: "backpack",
    label: "Спинной модуль",
    visualAnchor: "socket_backpack",
    accepts: ["backpack"],
  },
];

const modules: CatalogModule[] = [
  {
    id: "base.spider.light",
    label: "Лёгкая паучья база",
    role: "base",
    visualRef: "/assets/base.glb",
    attachmentPoints: baseTop(),
  },
  ...[
    ["base.spider.heavy", "Тяжёлая паучья база", "base-spider-heavy"],
    ["base.tracks.light", "Гусеничная база Mk I", "base-tracks-light"],
    ["base.tracks.heavy", "Гусеничная база Mk III", "base-tracks-heavy"],
    ["base.tank.light", "Танковое шасси Mk I", "base-tank-light"],
    ["base.carrier.light", "Шасси-транспортёр Mk I", "base-carrier-light"],
    ["base.buggy.light", "Колёсное шасси Mk I", "base-buggy-light"],
  ].map(([id, label, slug]): CatalogModule => ({
    id: id!,
    label: label!,
    role: "base",
    visualRef: `/assets/catalog/${slug}.glb`,
    attachmentPoints: baseTop(),
  })),
  ...[
    ["cockpit.gunner", "Кабина наводчика", "cockpit-gunner"],
    ["cockpit.flyer", "Кабина разведчика", "cockpit-flyer"],
    ["cockpit.gunbot", "Штурмовая кабина", "cockpit-gunbot"],
    ["cockpit.moto", "Кабина мото", "cockpit-moto"],
  ].map(([id, label, slug]): CatalogModule => ({
    id: id!,
    label: label!,
    role: "module",
    connectionType: "cockpit",
    visualRef: `/assets/catalog/${slug}.glb`,
    attachmentPoints: regularCockpitPoints(),
  })),
  {
    id: "cockpit.flat",
    label: "Плоская орудийная кабина",
    role: "module",
    connectionType: "cockpit",
    visualRef: "/assets/catalog/cockpit-flat.glb",
    attachmentPoints: [
      {
        id: "weapon-top",
        label: "Основное вооружение",
        visualAnchor: "socket_weapon_top",
        accepts: ["weapon"],
      },
      {
        id: "backpack",
        label: "Спинной модуль",
        visualAnchor: "socket_backpack",
        accepts: ["backpack"],
      },
      {
        id: "shoulder-left",
        label: "Левое плечо",
        visualAnchor: "socket_shoulder_l",
        accepts: ["half-shoulder"],
      },
      {
        id: "shoulder-right",
        label: "Правое плечо",
        visualAnchor: "socket_shoulder_r",
        accepts: ["half-shoulder"],
      },
    ],
  },
  {
    id: "backpack.gun",
    label: "Спинная турель",
    role: "module",
    connectionType: "backpack",
    visualRef: "/assets/catalog/backpack-gun.glb",
    attachmentPoints: [
      {
        id: "weapon-top",
        label: "Орудие турели",
        visualAnchor: "socket_weapon_top",
        accepts: ["weapon"],
      },
    ],
  },
  {
    id: "backpack.motor",
    label: "Лёгкий моторный блок",
    role: "module",
    connectionType: "backpack",
    visualRef: "/assets/catalog/backpack-motor.glb",
    attachmentPoints: [],
  },
  ...[
    ["backpack.frame", "Средняя рама", "backpack-frame"],
    ["backpack.tank", "Танковый спинной блок", "backpack-tank"],
  ].map(([id, label, slug]): CatalogModule => ({
    id: id!,
    label: label!,
    role: "module",
    connectionType: "backpack",
    visualRef: `/assets/catalog/${slug}.glb`,
    attachmentPoints: [
      {
        id: "antenna",
        label: "Антенна",
        visualAnchor: "socket_antenna",
        accepts: ["antenna"],
      },
    ],
  })),
  {
    id: "shoulder.cobra",
    label: "Плечевая рама «Кобра»",
    role: "module",
    connectionType: "shoulder-frame",
    visualRef: "/assets/catalog/shoulder-cobra.glb",
    attachmentPoints: [
      {
        id: "cockpit",
        label: "Центральная кабина",
        visualAnchor: "socket_cockpit",
        accepts: ["cockpit"],
      },
      {
        id: "weapon-left",
        label: "Левое вооружение",
        visualAnchor: "socket_weapon_l",
        accepts: ["weapon"],
      },
      {
        id: "weapon-right",
        label: "Правое вооружение",
        visualAnchor: "socket_weapon_r",
        accepts: ["weapon"],
      },
    ],
  },
  ...[
    ["half-shoulder.cobra", "Полуплечо «Кобра»", "half-shoulder-cobra"],
    ["shield.light", "Боковой щит Mk I", "shield-light"],
    ["shield.heavy", "Боковой щит Mk III", "shield-heavy"],
  ].map(([id, label, slug]): CatalogModule => ({
    id: id!,
    label: label!,
    role: "module",
    connectionType: "half-shoulder",
    visualRef: `/assets/catalog/${slug}.glb`,
    attachmentPoints: [
      {
        id: "weapon",
        label: "Плечевое вооружение",
        visualAnchor: "socket_weapon",
        accepts: ["weapon"],
      },
    ],
  })),
  ...[
    ["antenna.short", "Короткая антенна", "antenna-short"],
    ["antenna.long", "Длинная антенна", "antenna-long"],
  ].map(([id, label, slug]): CatalogModule => ({
    id: id!,
    label: label!,
    role: "module",
    connectionType: "antenna",
    visualRef: `/assets/catalog/${slug}.glb`,
    attachmentPoints: [],
  })),
  ...[
    ["weapon.double-gun-l1", "Спаренная пушка Mk I", "weapon-double-gun-1"],
    ["weapon.double-gun-l3", "Спаренная пушка Mk III", "weapon-double-gun-3"],
    ["weapon.sniper-l1", "Снайперская пушка Mk I", "weapon-sniper-1"],
    ["weapon.shock-rifle-l1", "Импульсная винтовка Mk I", "weapon-shock-rifle-1"],
    ["weapon.shocker-l1", "Электрошокер Mk I", "weapon-shocker-1"],
    ["weapon.grenade-l1", "Гранатомёт Mk I", "weapon-grenade-1"],
  ].map(([id, label, slug]): CatalogModule => ({
    id: id!,
    label: label!,
    role: "module",
    connectionType: "weapon",
    visualRef: `/assets/catalog/${slug}.glb`,
    attachmentPoints: [],
  })),
];

const presentationEntries: readonly [string, ModulePresentation][] = [
  ["base.spider.light", presentation("locomotion", "base-spider-light", "Legs_Spider_Lt", "spider")],
  ["base.spider.heavy", presentation("locomotion", "base-spider-heavy", "Legs_Spider_Hvy", "spider")],
  ["base.tracks.light", presentation("locomotion", "base-tracks-light", "Legs_Tracks_Lvl1", "tracks")],
  ["base.tracks.heavy", presentation("locomotion", "base-tracks-heavy", "Legs_Tracks_Lvl3", "tracks")],
  ["base.tank.light", presentation("locomotion", "base-tank-light", "Chassis_Tank_Lvl1", "tank")],
  ["base.carrier.light", presentation("locomotion", "base-carrier-light", "Chassis_Carrier_Lvl1", "tank")],
  ["base.buggy.light", presentation("locomotion", "base-buggy-light", "Buggy_Chassis_Lvl1", "buggy")],
  ["cockpit.gunner", presentation("cockpit", "cockpit-gunner", "Cockpit_Gunner")],
  ["cockpit.flyer", presentation("cockpit", "cockpit-flyer", "Cockpit_Flyer")],
  ["cockpit.gunbot", presentation("cockpit", "cockpit-gunbot", "Cockpit_Gunbot")],
  ["cockpit.moto", presentation("cockpit", "cockpit-moto", "Cockpit_Moto", "buggy")],
  ["cockpit.flat", presentation("cockpit", "cockpit-flat", "Cockpit_Gun_Flat_Full", "buggy")],
  ["backpack.gun", presentation("backpack", "backpack-gun", "Backpack_Gun")],
  ["backpack.motor", presentation("backpack", "backpack-motor", "Backpack_Lt_Motor", "buggy")],
  ["backpack.frame", presentation("backpack", "backpack-frame", "Backpack_Med_Frame", "buggy")],
  ["backpack.tank", presentation("backpack", "backpack-tank", "Backpack_Tank_Med", "tank")],
  ["shoulder.cobra", presentation("shoulder", "shoulder-cobra", "Shoulders_Med_Cobra")],
  ["half-shoulder.cobra", presentation("shoulder", "half-shoulder-cobra", "HalfShoulder_Cobra")],
  ["shield.light", presentation("shoulder", "shield-light", "Shoulder_Shield_Lvl1", "buggy")],
  ["shield.heavy", presentation("shoulder", "shield-heavy", "Shoulder_Shield_Lvl3", "buggy")],
  ["antenna.short", presentation("equipment", "antenna-short", "Antenna_Short")],
  ["antenna.long", presentation("equipment", "antenna-long", "Antenna_Long")],
  ["weapon.double-gun-l1", presentation("weapon", "weapon-double-gun-1", "Weapon_DoubleGun_lvl1")],
  ["weapon.double-gun-l3", presentation("weapon", "weapon-double-gun-3", "Weapon_DoubleGun_lvl3")],
  ["weapon.sniper-l1", presentation("weapon", "weapon-sniper-1", "Weapon_Sniper_lvl1")],
  ["weapon.shock-rifle-l1", presentation("weapon", "weapon-shock-rifle-1", "Weapon_Shock_Rifle_lvl1")],
  ["weapon.shocker-l1", presentation("weapon", "weapon-shocker-1", "Weapon_Shocker_lvl1")],
  ["weapon.grenade-l1", presentation("weapon", "weapon-grenade-1", "Weapon_GLauncher_lvl1")],
];

export const modulePresentation = new Map<string, ModulePresentation>(presentationEntries);

function presentation(
  category: CatalogCategoryId,
  slug: string,
  sourceId: string,
  family: ModulePresentation["family"] = "universal",
): ModulePresentation {
  return {
    category,
    previewRef: `/assets/previews/${slug}.png`,
    sourceId,
    family,
  };
}

export const spikeCatalog: Catalog = {
  id: "mech-constructor-browser-catalog",
  fingerprint: "browser-catalog-2026-08-25-r2",
  modules,
};

export const demonstrationRecipe: BuildRecipe = {
  schema: 1,
  catalogFingerprint: spikeCatalog.fingerprint,
  base: {
    positionId: "base-main",
    moduleId: "base.spider.light",
  },
  placements: [
    {
      positionId: "cockpit-main",
      parentPositionId: "base-main",
      attachmentPointId: "top",
      moduleId: "cockpit.gunner",
    },
    {
      positionId: "weapon-left-main",
      parentPositionId: "cockpit-main",
      attachmentPointId: "weapon-left",
      moduleId: "weapon.double-gun-l1",
    },
    {
      positionId: "weapon-right-main",
      parentPositionId: "cockpit-main",
      attachmentPointId: "weapon-right",
      moduleId: "weapon.double-gun-l1",
    },
  ],
  unresolved: [],
  livery: defaultLivery,
};
